import React, { useState } from 'react';

const VC_GRADES = [
  { id: 'full',    label: 'Full',    color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { id: 'partial', label: 'Partial', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  { id: 'synergy', label: 'Synergy', color: '#ea580c', bg: '#fff7ed', border: '#fdba74' },
  { id: 'trace',   label: 'Trace',   color: '#ef4444', bg: '#fef2f2', border: '#fca5a5' },
  { id: 'absent',  label: 'Absent',  color: '#991b1b', bg: '#fff1f2', border: '#fda4af' },
];

const VC_ROWS = [
  { id: 'upper', label: 'Upper Limb' },
  { id: 'lower', label: 'Lower Limb' },
];

const VoluntaryControlAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const getGrade = (region, side) => value[`${region}_${side}`] || null;
  const setGrade = (region, side, grade) => {
    const key = `${region}_${side}`;
    onChange?.({ ...value, [key]: value[key] === grade ? null : grade });
  };

  const filledCount = VC_ROWS.reduce((n, r) =>
    n + (['rt','lt'].filter(s => getGrade(r.id, s)).length), 0);
  const abnormalCount = VC_ROWS.reduce((n, r) =>
    n + (['rt','lt'].filter(s => {
      const g = getGrade(r.id, s);
      return g && g !== 'full';
    }).length), 0);

  const GradeCell = ({ region, side }) => {
    const selected = getGrade(region, side);
    const gr = VC_GRADES.find(g => g.id === selected);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
          {VC_GRADES.map(g => {
            const active = selected === g.id;
            return (
              <button key={g.id} type="button"
                onClick={() => setGrade(region, side, g.id)}
                style={{
                  fontSize: '11px', fontWeight: 700,
                  padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
                  transition: 'all 0.15s',
                  border: `1px solid ${active ? g.border : '#e2e8f0'}`,
                  background: active ? g.bg : 'transparent',
                  color: active ? g.color : '#64748b'
                }}>{active ? '✓ ' : ''}{g.label}</button>
            );
          })}
        </div>
        {gr && (
          <div style={{ fontSize: '10px', color: gr.color, fontWeight: 700, marginTop: '2px' }}>
            {gr.label} control
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px',
          borderRadius: '14px', border: '1px solid #e2e8f0',
          backgroundColor: 'transparent', color: '#166534',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
          transition: 'all 0.2s'
        }}>
          + Voluntary Control Assessment
          {filledCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#166534', color: '#fff', fontWeight: 800 }}>
              {filledCount}/4 assessed
            </span>
          )}
          {abnormalCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#dc2626', color: '#fff', fontWeight: 800 }}>
              {abnormalCount} impaired
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#166534' }}>💪 Voluntary Control Assessment</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = '#ef4444'}
              onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '14px',
            padding: '10px 16px', borderRadius: '12px',
            background: abnormalCount > 0 ? '#fef2f2' : '#ecfdf5',
            border: `1px solid ${abnormalCount > 0 ? '#fca5a5' : '#6ee7b7'}`,
            marginBottom: '14px', flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: abnormalCount > 0 ? '#dc2626' : '#059669' }}>
              {abnormalCount === 0 ? (filledCount === 0 ? 'Not yet assessed' : 'All assessed regions show full control') : `${abnormalCount} region${abnormalCount > 1 ? 's' : ''} with impaired voluntary control`}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {VC_GRADES.map(g => (
                <span key={g.id} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: g.bg, color: g.color, border: `1px solid ${g.border}`, fontWeight: 600 }}>
                  {g.label}
                </span>
              ))}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #bbf7d0', background: '#fff', minWidth: '360px' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', background: '#f0fdf4', borderBottom: '0.5px solid #bbf7d0', padding: '8px 14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#166534' }}>Region</div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#166534', textAlign: 'center' }}>Right (RL)</div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#166534', textAlign: 'center' }}>Left (LL)</div>
            </div>
            {VC_ROWS.map((row, idx) => (
              <div key={row.id} style={{
                display: 'grid', gridTemplateColumns: '160px 1fr 1fr',
                alignItems: 'center', padding: '12px 14px',
                borderBottom: idx < VC_ROWS.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                background: (getGrade(row.id, 'rt') || getGrade(row.id, 'lt')) ? '#fafffe' : '#fff'
              }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{row.label}</div>
                <div style={{ borderRight: '0.5px solid #f1f5f9', paddingRight: '10px' }}>
                  <GradeCell region={row.id} side="rt" />
                </div>
                <div style={{ paddingLeft: '10px' }}>
                  <GradeCell region={row.id} side="lt" />
                </div>
              </div>
            ))}
          </div>

          </div>

          {filledCount > 0 && (
            <button type="button" onClick={() => onChange({})} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
              Clear all voluntary control data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VoluntaryControlAssessment;
